import { useEffect, useState } from 'react';
import { usePage } from '../../context/PageContext';

import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useParams } from 'react-router-dom';
import { ROUTES, SystemConstants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarOutline, Cross, Slash } from '../icons';
import { useFetchTaskDetail } from '../../hooks/useFetchTaskDetail';
import { useTask } from '../../context/TaskContext';
import { ITask, IUpdateTask } from '../../interface/tasks';
import {
  chosenTimeOfDay,
  extractDate,
  extractTime,
  getChangedFields,
  mergeDateAndTime,
} from '../../constants/utilityFunctions';
import { showToast } from '../utils/toast';
import { useTaskMutations } from '../../hooks/useTaskMutations';
import { useUser } from '../../context/UserContext';
import { Button, Form, FormItem, LookupFieldRow, Spin } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import { TimePicker } from '../Ant/TimePicker';
import Tag from '../tag/tag';
import { taskFields, taskRelations, TasksPriority } from '../../constants/tasks';
import { useFetchActivityStatus } from '../../hooks/useFetchActivityStatus';
import CustomDatePicker from '../Ant/CustomDatePicker/CustomDatePicker';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomModal from '../informationPopup/customModal';
import ApiErrorModal from '../informationPopup/apiErrorModal';
import FieldErrorMessage from '../fieldErrorMessage/fieldErrorMessage';
import { useTaskFieldError } from '../../hooks/useTaskFieldError';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { isCriticalTaskFormChanged } from './isCriticalTaskFormChanged';
import SearchableSelect from '../searchableSelect/searchableSelect';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import TaskStatusSelect from './taskStatusSelect';
import TaskMatterSelect from './taskMatterSelect';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

/** Critical tasks are always high priority, so the locked field has nothing to change. */
const ignorePriorityChange = () => undefined;

const AddCriticalTask = () => {
  const { setTitle } = usePage();
  const { user } = useUser();
  const { push, goBack } = useNavigationStack();
  const { t } = useTranslation();
  const { id: taskId } = useParams();
  const { trackPage } = useAnalytics();
  const { task, updateTask, initialTask, clearInitialTask } = useTask();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const {
    handleAddTask,
    handleEditTask,
    isEmailTagLoading,
    taskFieldError,
    clearTaskFieldError,
    isTaskError,
    taskErrorMessage,
    closeTaskErrorModal,
  } = useTaskMutations({ taskId, isCriticalTask: true, handleBack: () => handleBack() });
  const { errorFieldRef, matterErrorMessage } = useTaskFieldError({
    taskFieldError,
    clearTaskFieldError,
    matterId: task?.matter?.id,
  });

  const { data: taskDetails, isLoading: isTaskDetailLoading } = useFetchTaskDetail({
    id: !task ? (taskId ?? '') : '',
    relations: [...taskRelations, 'otherResponsibleUsers.user:fullName'],
    fields: taskFields,
  });
  const { data: statusList } = useFetchActivityStatus({ pageSize: 50 });

  useEffect(() => {
    trackPage(AnalyticsPage.CRITICAL_TASK_ADD_EDIT);
  }, [trackPage]);

  const defaultStatus = statusList.find(
    (status) => status.id === SystemConstants.TASK_STATUS_NOT_STARTED,
  );
  const priorityOption: SelectOption = {
    value: TasksPriority.high,
    label: t(`task:priorityLevel.${TasksPriority.high}`),
  };
  const handleBack = () => {
    updateTask(null);
    clearInitialTask();
    goBack();
  };
  const handleTaskStatuses = () => {
    push(ROUTES.TASK_STATUSES, {
      fromCritical: true,
      returnFocusTo: 'status',
    });
  };
  const handleSelectMatter = () => {
    push(ROUTES.TASK_MATTER_SELECTION, {
      fromCritical: true,
      returnFocusTo: 'matter',
    });
  };
  const handleSelectPerson = () => {
    push(ROUTES.PERSON_LISTING, { returnFocusTo: 'otherResponsiblePersons' });
  };
  const validateFields = () => {
    if (
      !task?.currentSubject ||
      !task?.dueTime ||
      !task?.startTime ||
      !task?.statusId ||
      !task?.alertTime
    ) {
      return true;
    }
    return false;
  };

  const handleTaskSave = () => {
    if (validateFields()) {
      showToast('info', t('common:fillOutAllRequiredFields'));
      return;
    }
    if (task?.id) {
      handleEditTask(buildChangedTaskPayload());
    } else {
      handleAddTask(generateBasePayload());
    }
  };
  const generateBasePayload = (): IUpdateTask => {
    return {
      version: taskId ? task?.version : undefined,
      completedTime: task?.completedTime,
      subject: task?.currentSubject,
      statusId: task?.statusId,
      description: task?.description,
      startTime: task?.startTime,
      dueTime: task?.dueTime,
      alertTime: task?.alertTime,
      priority: TasksPriority.high,
      matterId: task?.matterId,
      responsibleUserId: !taskId ? user?.id : undefined,
      otherResponsibleUsers:
        (task?.otherResponsibleUsers ?? [])?.map((user) => {
          return {
            userId: user.user.id,
          };
        }) ?? [],
      activityTypeId: !taskId ? SystemConstants.CRITICAL_TASK_ID : undefined,
    };
  };
  const buildChangedTaskPayload = (): IUpdateTask => {
    const changedPayload = getChangedFields(
      initialTask as unknown as IUpdateTask,
      generateBasePayload(),
    );
    return {
      ...changedPayload,
      version: initialTask?.version ?? '0',
    } as IUpdateTask;
  };
  useEffect(() => {
    if (!isTaskDetailLoading) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={onBack}
          showCross
          validateOnFormChanged={promptDiscardIfFormChanged}
          title={!task?.id ? t('task:criticalTask.addNew') : (task?.subject ?? '')}
          subTitle={!task?.id ? '' : t('task:criticalTask.edit')}
        />,
      );
    }
  }, [task, taskId, location?.pathname, isTaskDetailLoading]);
  useEffect(() => {
    if (taskDetails) {
      updateTask({
        ...taskDetails,
        currentSubject: taskDetails?.subject,
        isAlertFlag: !!taskDetails?.alertTime,
      });
    } else {
      updateTask({
        ...task,
        status: task?.status ?? defaultStatus,
        statusId: task?.status?.id ?? defaultStatus?.id,
      } as ITask);
    }
  }, [taskDetails, location?.pathname, defaultStatus]);

  const handleRemoveTag = (tagId: string) => {
    const updatedTags = (task?.otherResponsibleUsers ?? [])?.filter(
      (tag) => tag?.user?.id !== tagId,
    );
    updateTask({
      ...task,
      otherResponsibleUsers: updatedTags,
    } as ITask);
  };
  const onBack = () => {
    promptDiscardIfFormChanged(() => {
      handleBack();
    });
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  // Function to prompt discard modal if form changed, or proceed directly
  const promptDiscardIfFormChanged = (onProceed: () => void) => {
    const isFormChanged = isCriticalTaskFormChanged(task, initialTask, !!taskId);
    if (!isFormChanged) {
      onProceed?.();
      return;
    }
    setShowDiscardDialog(true);
    setOnDiscardConfirm(() => onProceed);
  };
  if (isTaskDetailLoading || isEmailTagLoading) return <Spin fullscreen />;

  return (
    <div className="h-full w-full pb-gutter add-transaction-form">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <FormItem name="subject" label={t('common:subject')} required>
            <div className="relative">
              <TextAreaField
                data-testid="subject-input"
                maxLength={500}
                value={task?.currentSubject ?? ''}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateTask({
                    ...task,
                    currentSubject: e?.target?.value ?? '',
                  } as ITask);
                }}
              />
            </div>
          </FormItem>

          <FormItem label={t('task:description')}>
            <TextAreaField
              data-testid="description-input"
              value={task?.description ?? ''}
              onChange={(e) => {
                updateTask({
                  ...task,
                  description: e.target.value,
                } as ITask);
              }}
              maxLength={1000}
              placeholder={t('common:addDescription')}
            />
          </FormItem>
          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('task:startDate')} required={true}>
              <CustomDatePicker
                data-testid="start-date-picker"
                className="w-full"
                placeholder={t('transaction:date')} // Placeholder text
                value={task?.startTime ? dayjs(extractDate(task?.startTime)) : undefined} // Convert value to dayjs object
                onChange={(date) => {
                  const newStartTime = mergeDateAndTime(
                    date?.toDate(), // Convert dayjs object to Date
                    extractTime(task?.dueTime)!,
                  );
                  let updatedDueTime = task?.dueTime;
                  if (
                    updatedDueTime &&
                    newStartTime &&
                    new Date(newStartTime) > new Date(updatedDueTime)
                  ) {
                    updatedDueTime = newStartTime.toISOString();
                  }
                  updateTask({
                    ...task,
                    startTime: newStartTime,
                    dueTime: updatedDueTime,
                  } as unknown as ITask);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem required={true} className="w-full" label={t('task:startTime')}>
              <TimePicker
                data-testid="start-time-picker"
                className="w-full"
                format="HH:mm"
                placeholder="00:00"
                value={chosenTimeOfDay(task?.startTime, !!taskId)}
                onChange={(time) => {
                  const newStartTime = mergeDateAndTime(
                    extractDate(task?.startTime)!,
                    time?.toDate() as Date, // Convert dayjs object to Date
                  );
                  let updatedDueTime = task?.dueTime;
                  if (
                    updatedDueTime &&
                    newStartTime &&
                    new Date(newStartTime) > new Date(updatedDueTime)
                  ) {
                    updatedDueTime = newStartTime.toISOString();
                  }
                  updateTask({
                    ...task,
                    startTime: newStartTime.toISOString(),
                    dueTime: updatedDueTime,
                  } as ITask);
                }}
                allowClear={false}
                placement="bottomLeft"
                classNames={{ popup: { root: 'custom-time-picker' } }}
                getPopupContainer={(trigger) =>
                  (trigger.parentNode as HTMLElement) || document.body
                }
                minuteStep={1}
                showNow={false}
              />
            </FormItem>
          </Flex>

          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('task:dueDate')} required={true}>
              <CustomDatePicker
                data-testid="due-date-picker"
                className="w-full"
                placeholder={t('task:dateInputPlaceholder')} // Placeholder text
                value={task?.dueTime ? dayjs(extractDate(task?.dueTime)) : undefined} // Convert value to dayjs object
                disabledDate={(current) => {
                  return task?.startTime ? current?.isBefore(dayjs(task?.startTime), 'day') : false;
                }}
                onChange={(date) => {
                  const newDueTime = mergeDateAndTime(
                    date?.toDate(), // Convert dayjs object to Date
                    extractTime(task?.startTime) || extractTime(task?.dueTime)!, // Use start time if available, else existing due time
                  );
                  updateTask({ ...task, dueTime: newDueTime.toISOString() } as ITask);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem required={true} className="w-full" label={t('task:endTime')}>
              <TimePicker
                data-testid="end-time-picker"
                className="w-full"
                format="HH:mm"
                placeholder="00:00"
                value={chosenTimeOfDay(task?.dueTime, !!taskId)}
                disabledTime={() => {
                  if (!task?.startTime) {
                    return {
                      disabledHours: () => [],
                      disabledMinutes: () => [],
                    };
                  }

                  const startTime = dayjs(task.startTime);
                  const startHour = startTime.hour();
                  const startMinute = startTime.minute();
                  return {
                    disabledHours: () => [...new Array(startHour).keys()],
                    disabledMinutes: (selectedHour: number) => {
                      if (selectedHour === startHour) {
                        return [...new Array(startMinute).keys()];
                      }
                      return [];
                    },
                  };
                }}
                onChange={(time) => {
                  const newDueTime = mergeDateAndTime(
                    extractDate(task?.dueTime)!,
                    time?.toDate(), // Convert dayjs object to Date
                  );
                  updateTask({ ...task, dueTime: newDueTime.toISOString() } as ITask);
                }}
                allowClear={false}
                placement="bottomLeft"
                classNames={{ popup: { root: 'custom-time-picker' } }}
                getPopupContainer={(trigger) =>
                  (trigger.parentNode as HTMLElement) || document.body
                }
                minuteStep={1} // Equivalent to `interval={1}`
                showNow={false}
              />
            </FormItem>
          </Flex>
          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('task:alertDate')} required={true}>
              <CustomDatePicker
                className="w-full"
                data-testid="alert-date-picker"
                placeholder={t('task:dateInputPlaceholder')} // Placeholder text
                value={task?.alertTime ? dayjs(extractDate(task?.alertTime)) : undefined} // Convert value to dayjs object
                onChange={(date) => {
                  const newAlertTime = mergeDateAndTime(
                    date?.toDate(), // Convert dayjs object to Date
                    extractTime(task?.alertTime)!,
                  );
                  updateTask({ ...task, alertTime: newAlertTime.toISOString() } as ITask);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem required={true} className="w-full" label={t('task:alertTime')}>
              <TimePicker
                className="w-full"
                format="HH:mm"
                data-testid="alert-time-picker"
                placeholder="00:00"
                value={chosenTimeOfDay(task?.alertTime, !!taskId)}
                onChange={(time) => {
                  const newAlertTime = mergeDateAndTime(
                    extractDate(task?.alertTime)!,
                    time?.toDate() as Date, // Convert dayjs object to Date
                  );
                  updateTask({ ...task, alertTime: newAlertTime.toISOString() } as ITask);
                }}
                allowClear={false}
                placement="bottomLeft"
                classNames={{ popup: { root: 'custom-time-picker' } }}
                getPopupContainer={(trigger) =>
                  (trigger.parentNode as HTMLElement) || document.body
                }
                minuteStep={1} // Equivalent to `interval={1}`
                showNow={false}
              />
            </FormItem>
          </Flex>

          <FormItem label={t('task:status')} required={true}>
            <div className="relative">
              <TaskStatusSelect onOpenPicker={handleTaskStatuses} />
            </div>
          </FormItem>

          <FormItem label={t('task:priority')} name="priority" className="disabled">
            <div className="relative">
              <SearchableSelect
                options={[priorityOption]}
                value={priorityOption}
                onChange={ignorePriorityChange}
                disabled
                ariaLabel={t('task:priority')}
                placeholder={t('task:selectPriority')}
                data-testid="priority-button"
              />
            </div>
          </FormItem>

          <FormItem
            data-testid="critical-task-matter-form-item"
            label={t('matter:matter')}
            name="matter"
            validateStatus={matterErrorMessage ? 'error' : undefined}
            className={task?.disableMatterChange ? 'disabled' : undefined}
            extra={
              matterErrorMessage ? (
                <FieldErrorMessage ref={errorFieldRef} text={matterErrorMessage} />
              ) : null
            }
          >
            <div className="relative">
              <TaskMatterSelect
                disabled={task?.disableMatterChange}
                onOpenPicker={handleSelectMatter}
                onClear={() =>
                  updateTask({
                    ...task,
                    matter: undefined,
                    matterId: undefined,
                  } as unknown as ITask)
                }
              />
            </div>
          </FormItem>
          <FormItem label={t('task:otherResponsiblePersons')}>
            <div className="relative">
              {/* Each chosen person carries its own remove button, so once there are any the
                  trigger renders as a row rather than a button, which cannot hold one. Keyboard
                  and screen reader users reach the picker through "Change" beside it. */}
              {(task?.otherResponsibleUsers ?? []).length > 0 ? (
                <>
                  <LookupFieldRow className="add-transaction-btn" onClick={handleSelectPerson}>
                    <div className="d-flex gap-5 flex-wrap ">
                      {task?.otherResponsibleUsers.map((tag) => {
                        return (
                          <Tag
                            text={tag.user.fullName}
                            removeTag={() => handleRemoveTag(tag?.user.id ?? '')}
                            classname="person-tag"
                            icon={<Cross />}
                            key={tag?.user?.id}
                          />
                        );
                      })}
                    </div>
                  </LookupFieldRow>
                  <Button
                    data-testid="change-person-button"
                    data-field="otherResponsiblePersons"
                    type="text"
                    className="change-value-btn"
                    onClick={handleSelectPerson}
                  >
                    {t('common:change')}
                  </Button>
                </>
              ) : (
                <Button
                  type="default"
                  data-field="otherResponsiblePersons"
                  className="add-transaction-btn"
                  block
                  onClick={handleSelectPerson}
                >
                  <div
                    data-testid="select-person-button"
                    className="d-flex justify-between w-full items-center preselection-field"
                  >
                    <span>{t('task:selectPersons')}</span>
                    <ArrowRight />
                  </div>
                </Button>
              )}
            </div>
          </FormItem>

          <PinnedSaveButton
            data-testid="save-button"
            disabled={validateFields()}
            onClick={handleTaskSave}
          >
            {taskId ? t('common:saveChanges') : t('task:criticalTask.addNew')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
      <ApiErrorModal
        data-testid="critical-task-error-modal"
        visible={isTaskError}
        message={taskErrorMessage}
        onClose={closeTaskErrorModal}
      />
    </div>
  );
};

export default AddCriticalTask;
